import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { MotionConfig } from 'motion/react';
import Join from '@/screens/Join';
import { isValidCode } from '@shared/codes';
import Play from '@/screens/Play';
import Result from '@/screens/Result';
import Details from '@/screens/Details';

// The staff tools, the stage and the legal pages load on demand, so the player's bundle stays small (spec section 11).
const Host = lazy(() => import('@/screens/Host'));
const Stage = lazy(() => import('@/screens/Stage'));
const StageLive = lazy(() => import('@/screens/StageLive'));
// 17 Oct 2026: the presenter's On air window for an evening of battles (#key= in the link).
const OnAir = lazy(() => import('@/screens/OnAir'));
// 30 Oct 2026: the presenter's Console for the same evening (#key= in the link).
const Present = lazy(() => import('@/screens/Present'));
const Studio = lazy(() => import('@/screens/Studio'));
const OpsList = lazy(() => import('@/screens/OpsList'));
const Ops = lazy(() => import('@/screens/Ops'));
const Terms = lazy(() => import('@/screens/legal/Terms'));
const Privacy = lazy(() => import('@/screens/legal/Privacy'));
// Sign-in on Cognito (the address, then the emailed code) and the staff pages in the shell. The gate lets in a signed-in session or a #key= link, else sends people to sign in.
const StaffGate = lazy(() => import('@/staff/StaffGate'));
const SignIn = lazy(() => import('@/auth/SignIn'));
const Register = lazy(() => import('@/auth/Register'));
const Account = lazy(() => import('@/auth/Account'));
const Home = lazy(() => import('@/auth/Home'));
const Team = lazy(() => import('@/staff/Team'));
const Accounts = lazy(() => import('@/staff/Accounts'));
const Audit = lazy(() => import('@/staff/Audit'));
const StagePage = lazy(() => import('@/staff/StagePage'));
const BattlesPage = lazy(() => import('@/staff/BattlesPage'));
const SettingsPage = lazy(() => import('@/staff/SettingsPage'));
const DailyPage = lazy(() => import('@/staff/DailyPage'));
// Creator mode: any signed-in account builds and hosts quick battles; the open list is public.
const Create = lazy(() => import('@/creator/Create'));
const MyBattles = lazy(() => import('@/creator/MyBattles'));
const OpenBattles = lazy(() => import('@/creator/OpenBattles'));
const League = lazy(() => import('@/screens/League'));
// 30 Oct 2026: the front page (marketing, the practice round, the founding list) is its own chunk: the player's path
// (/j/CODE, /play) never loads it. The join form it replaced at / lives at /join.
const Landing = lazy(() => import('@/landing/Landing'));
// Part A (3 Nov 2026): the daily loop's pages, for signed-in accounts.
const Boards = lazy(() => import('@/loop/Boards'));
const Profile = lazy(() => import('@/loop/Profile'));

/** Shown only if a page takes a moment to load, so a fast load never flashes. */
function Loading() {
  const [shown, setShown] = useState(false);
  useEffect(() => { const t = window.setTimeout(() => setShown(true), 400); return () => window.clearTimeout(t); }, []);
  return (
    <div className="flex min-h-full items-center justify-center p-6" aria-busy="true">
      <p className={`text-sm text-ink-3 transition-opacity duration-300 ${shown ? 'opacity-100' : 'opacity-0'}`}>Loading</p>
    </div>
  );
}

/** A page's code can fail to load (a new version was deployed while the tab was open, or the network dropped): offer a reload. */
class PageBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: unknown) { console.error(error); }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-4 px-4 py-10">
        <h1 className="font-display text-3xl font-extrabold text-ink">This page could not load.</h1>
        <p className="text-ink-2">It may have been updated since you opened it. Reloading fetches the latest version.</p>
        <button type="button" onClick={() => window.location.reload()}
          className="inline-flex min-h-12 w-fit items-center rounded-tile bg-brand px-5 font-semibold text-white focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-iv">Reload</button>
      </main>
    );
  }
}

const later = (page: ReactNode) => <PageBoundary><Suspense fallback={<Loading />}>{page}</Suspense></PageBoundary>;
/** A MedSnapp staff page: signed in, or opened with the shared ops key link while the server still takes it. */
const staff = (page: ReactNode) => later(<StaffGate>{page}</StaffGate>);

/**
 * The old sign-in addresses (in bookmarks and old emails) go to the new page, keeping where they were going. An old
 * email link carried its token and `next` in the fragment: only `next` travels on; the token goes no further.
 */
function ToSignIn() {
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get('next') ?? new URLSearchParams(loc.hash.replace(/^#/, '')).get('next');
  return <Navigate to={`/sign-in${next ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
}

/**
 * 13 Oct: the short link players type from a Live, address/ABCDE: a five-character battle code at the root goes to that
 * battle's join page, keeping any ?ref=. 30 Oct 2026: anything else at the root (/j, /LOGIN, a mistyped code) goes to
 * the join form at /join, with its code box: rendered here, Join read the segment as the code and hid the box.
 */
/** Words that read as codes but are pages people type (a battle with one of these codes still joins at /j/CODE). */
const NOT_CODES = new Set(['STAFF', 'ADMIN', 'STAGE', 'LOGIN']);
function ShortJoin() {
  const loc = useLocation();
  const code = loc.pathname.replace(/^\/+|\/+$/g, '');
  return isValidCode(code) && !NOT_CODES.has(code.toUpperCase())
    ? <Navigate to={`/j/${code.toUpperCase()}${loc.search}`} replace />
    : <Navigate to={`/join${loc.search}`} replace />;
}

/**
 * 14 Oct: a battle's join page, mounted afresh for each code. Without the key React keeps the front page's join screen
 * when a link moves from / to /j/CODE (the same component in the same place), with the empty code it started with, so
 * the join was refused ("That code should be five letters or numbers"); the front page's live-battle card found it.
 */
export function JoinAt() {
  const { code = '' } = useParams();
  return <Join key={code.toUpperCase()} />;
}

/** Every route. Exported so the routing itself is tested (App wraps it in the browser router). */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={later(<Landing />)} />
      {/* Declared above /:code, which would otherwise take any single-segment path. */}
      <Route path="/join" element={<Join />} />
      <Route path="/j/:code" element={<JoinAt />} />
      <Route path="/play" element={<Play />} />
      <Route path="/details" element={<Details />} />
      <Route path="/r/:token" element={<Result />} />
      {/* The result page on this device's session (the podium's button before GET /me has handed it the read-only link). */}
      <Route path="/r" element={<Result />} />
      <Route path="/terms" element={later(<Terms />)} />
      <Route path="/terms/:code" element={later(<Terms />)} />
      <Route path="/privacy" element={later(<Privacy />)} />
      <Route path="/host/:code" element={later(<Host />)} />
      <Route path="/stage/live" element={later(<StageLive />)} />
      <Route path="/stage/:code" element={later(<Stage />)} />
      <Route path="/onair/:slug" element={later(<OnAir />)} />
      <Route path="/present/:slug" element={later(<Present />)} />
      {/* The Battle builder: /builder is its name for people, /studio keeps working for links already shared. */}
      <Route path="/builder" element={staff(<Studio />)} />
      <Route path="/studio" element={staff(<Studio />)} />
      <Route path="/ops" element={staff(<OpsList />)} />
      <Route path="/ops/:code" element={staff(<Ops />)} />
      <Route path="/staff/stage" element={staff(<StagePage />)} />
      <Route path="/staff/battles" element={staff(<BattlesPage />)} />
      <Route path="/staff/settings" element={staff(<SettingsPage />)} />
      <Route path="/staff/daily" element={later(<DailyPage />)} />
      <Route path="/sign-in" element={later(<SignIn />)} />
      <Route path="/register" element={later(<Register />)} />
      <Route path="/account" element={later(<Account />)} />
      <Route path="/home" element={later(<Home />)} />
      <Route path="/staff/sign-in" element={<ToSignIn />} />
      <Route path="/staff/verify" element={<ToSignIn />} />
      <Route path="/staff/team" element={later(<Team />)} />
      {/* Admin only, like Team: the page gates itself (AdminOnly), so the shared ops link never opens it. */}
      <Route path="/staff/accounts" element={later(<Accounts />)} />
      <Route path="/staff/audit" element={later(<Audit />)} />
      <Route path="/create" element={later(<Create />)} />
      <Route path="/create/battles" element={later(<MyBattles />)} />
      <Route path="/open" element={later(<OpenBattles />)} />
      <Route path="/league" element={later(<League />)} />
      <Route path="/league/:slug" element={later(<League />)} />
      <Route path="/boards" element={later(<Boards />)} />
      <Route path="/profile" element={later(<Profile />)} />
      <Route path="/:code" element={<ShortJoin />} />
      <Route path="*" element={<Join />} />
    </Routes>
  );
}

export default function App() {
  return (
    // Motion follows the viewer's reduced-motion setting on every page (design system README).
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </MotionConfig>
  );
}
